import React, { useEffect } from "react";
import { observer } from "mobx-react";
import { useInjection } from "inversify-react";
import { AppController } from "@dropDesk/presentation/app/app.controller";
import DropDeskLoading from "@dropDesk/presentation/components/loadings/loading_dropdesk";
import toastMessage from "@dropDesk/utils/toast_message/toast_message";
import { t } from "i18next";
import { ICONS_DROPDESK } from "@dropDesk/storage/icons/icons.d";
import {
    ReportTicketComparativeController
} from "@dropDesk/presentation/pages/reports/report_ticket_init/report_ticket_comparative/controller/report_ticket_comparative.controller";
import SearchBar from "@dropDesk/presentation/pages/reports/report_ticket_init/report_ticket_comparative/ui/search_bar";
import HeaderComparative from "@dropDesk/presentation/pages/reports/report_ticket_init/report_ticket_comparative/ui/header";
import MetricsCards from "@dropDesk/presentation/components/metrics_cards";
import RankingTable from "@dropDesk/presentation/pages/reports/report_ticket_init/report_ticket_comparative/ui/ranking_table";
import FeaturedAttendants from "@dropDesk/presentation/pages/reports/report_ticket_init/report_ticket_comparative/ui/featured_attendants";
import SummaryBanner from "@dropDesk/presentation/components/summary_banner";
import ModalsReportTicketComparative
    from "@dropDesk/presentation/pages/reports/report_ticket_init/report_ticket_comparative/ui/modals";
import FiltersApplied, {
    AppliedFilterItem,
    FilterKey
} from "@dropDesk/presentation/components/filters/filters_applied";
import { Container, ContainerFiltersApplied, Content, Sections, SectionsRow } from "./styled";

const ReportTicketsComparative = observer(() => {
    const appController = useInjection(AppController);
    const colors = appController.theme.colorScheme;
    const controller = useInjection(ReportTicketComparativeController);

    useEffect(() => {
        controller.initialize();
    }, []);

    const onSuccess = (key: string) => {
        toastMessage.success(t(key));
    };

    const appliedFilters: AppliedFilterItem[] = [];

    if (controller.filterClient) {
        appliedFilters.push({
            key: "client",
            label: "Empresa",
            value: controller.filterClient.socialName || controller.filterClient.name,
        });
    }
    if (controller.filterUserClient) {
        appliedFilters.push({
            key: "userClient",
            label: "Contato",
            value: controller.filterUserClient.name,
        });
    }
    if (controller.filterSector) {
        appliedFilters.push({
            key: "sector",
            label: "Setor",
            value: controller.filterSector.realName(appController.user!),
        });
    }
    if (controller.filterAttendant) {
        appliedFilters.push({
            key: "attendant",
            label: "Atendente",
            value: controller.filterAttendant.name,
        });
    }
    if (controller.filterDescriptionStatic) {
        appliedFilters.push({
            key: "descriptionStatic",
            label: "Problema",
            value: controller.filterDescriptionStatic.description,
        });
    }

    const handleRemoveFilter = (key: FilterKey) => {
        switch (key) {
            case "client":
                controller.setFilterClient(null);
                break;
            case "userClient":
                controller.setFilterUserClient(null);
                break;
            case "sector":
                controller.setFilterSector(null);
                break;
            case "attendant":
                controller.setFilterAttendant(null);
                break;
            case "descriptionStatic":
                controller.setFilterDescriptionStatic(null);
                break;
        }
    };

    const isDataLoading = controller.loading
        && controller.loadingMessage !== "report_ticket_comparative.export_html";
    const isExportLoading = controller.loading
        && controller.loadingMessage === "report_ticket_comparative.export_html";

    return (
        <Container background={colors.onBackground} heightShowingHeaderInfo={appController.heightShowingHeaderInfo}>
            <HeaderComparative
                loading={controller.loading}
                hasReport={!!controller.report}
                filterClient={controller.filterClient}
                filterUserClient={controller.filterUserClient}
                filterSector={controller.filterSector}
                filterAttendant={controller.filterAttendant}
                filterDescriptionStatic={controller.filterDescriptionStatic}
                setFilterClient={(f) => controller.setFilterClient(f)}
                setFilterUserClient={(f) => controller.setFilterUserClient(f)}
                setFilterSector={(f) => controller.setFilterSector(f)}
                setFilterAttendant={(f) => controller.setFilterAttendant(f)}
                setFilterDescriptionStatic={(f) => controller.setFilterDescriptionStatic(f)}
                onClearFilters={() => controller.clearEntityFilters()}
            />
            {appliedFilters.length > 0 && (
                <ContainerFiltersApplied>
                    <FiltersApplied
                        items={appliedFilters}
                        onRemoveItem={handleRemoveFilter}
                        onClearAll={() => controller.clearEntityFilters()}
                        paddingWidth={40}
                    />
                </ContainerFiltersApplied>
            )}
            <SearchBar
                period={controller.period}
                comparePeriod={controller.comparePeriod}
                preset={controller.preset}
                dateErrors={controller.dateErrors}
                setPeriodStart={(date) => controller.setPeriodStart(date)}
                setPeriodEnd={(date) => controller.setPeriodEnd(date)}
                setComparePeriodStart={(date) => controller.setComparePeriodStart(date)}
                setComparePeriodEnd={(date) => controller.setComparePeriodEnd(date)}
                applyPreset={(preset) => controller.applyPreset(preset)}
            />
            <Content>
                {(isDataLoading || !!controller.report) && (
                    <Sections>
                        <MetricsCards
                            metrics={controller.report?.metrics}
                            normalization={controller.report?.normalization ?? 'daily'}
                            loading={isDataLoading}
                        />
                        <SectionsRow>
                            <RankingTable
                                title="Atendimentos por setor — comparativo"
                                items={controller.report?.sectors ?? []}
                                rowIcon={ICONS_DROPDESK.sitemap}
                                nameHeader="Setor"
                                highlight={controller.report?.texts.sectorsHighlight}
                                loading={isDataLoading}
                            />
                            <RankingTable
                                title="Problemas pré-definidos — comparativo"
                                items={controller.report?.descriptionStatics ?? []}
                                rowIcon={ICONS_DROPDESK.staticProblem}
                                nameHeader="Problema"
                                highlight={controller.report?.texts.problemsHighlight}
                                loading={isDataLoading}
                            />
                        </SectionsRow>
                        <SectionsRow>
                            <RankingTable
                                title="Empresas com mais atendimentos - comparativo"
                                items={controller.report?.clients ?? []}
                                rowIcon={ICONS_DROPDESK.business}
                                nameHeader="Empresa"
                                highlight={controller.report?.texts.clientsHighlight}
                                loading={isDataLoading}
                            />
                            <FeaturedAttendants
                                attendants={controller.report?.featuredAttendants ?? []}
                                highlight={controller.report?.texts.attendantsHighlight}
                                loading={isDataLoading}
                            />
                        </SectionsRow>
                        {!!controller.report && !isDataLoading && (
                            <SummaryBanner
                                title="Resumo do comparativo"
                                summary={controller.report.texts.summary}
                            />
                        )}
                    </Sections>
                )}
            </Content>
            <ModalsReportTicketComparative
                loading={controller.loading}
                exportHtml={() => controller.exportHtml(onSuccess)}
            />
            {isExportLoading && (
                <DropDeskLoading
                    height={250}
                    description={controller.loadingMessage ?? "report_ticket_comparative.export_html"}
                />
            )}
        </Container>
    );
});

export default ReportTicketsComparative;
